import { useState, useEffect } from "react";
import axios from "axios";
import { Fish, Waves, MapPin, MessageSquare, Calendar, Star, Users, Anchor, FileText } from "lucide-react";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

// Entity link format: [[type:id]]
// e.g. [[species:abc-123]], [[report:xyz-456]], [[trip:def-789]], [[thread:ghi-012]]
const ENTITY_LINK_REGEX = /\[\[(species|report|trip|thread):([a-f0-9-]+)\]\]/g;

// Cache for entity previews to avoid redundant API calls
const previewCache = {};

const ENTITY_ICONS = {
  species: Fish,
  report: Waves,
  trip: Anchor,
  thread: MessageSquare,
};

const ENTITY_COLORS = {
  species: { bg: "bg-cyan-50", border: "border-cyan-200", text: "text-cyan-700", hover: "hover:bg-cyan-100", accent: "bg-cyan-500" },
  report: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", hover: "hover:bg-blue-100", accent: "bg-blue-500" },
  trip: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", hover: "hover:bg-emerald-100", accent: "bg-emerald-500" },
  thread: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", hover: "hover:bg-violet-100", accent: "bg-violet-500" },
};

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
};

// Reusable mini card for any entity type
export const EntityMiniCard = ({ type, id, onClick, className = "" }) => {
  const [preview, setPreview] = useState(previewCache[`${type}:${id}`] || null);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (preview) return;
    const cacheKey = `${type}:${id}`;
    if (previewCache[cacheKey]) {
      setPreview(previewCache[cacheKey]);
      setLoading(false);
      return;
    }

    const fetchPreview = async () => {
      try {
        const res = await axios.get(`${API}/entity/preview/${type}/${id}`);
        previewCache[cacheKey] = res.data;
        setPreview(res.data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPreview();
  }, [type, id, preview]);

  const colors = ENTITY_COLORS[type] || ENTITY_COLORS.species;
  const Icon = ENTITY_ICONS[type] || Fish;

  if (error) return null;

  if (loading) {
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md ${colors.bg} ${colors.border} border text-xs ${colors.text} animate-pulse ${className}`}>
        <Icon className="w-3.5 h-3.5" />
        Loading...
      </span>
    );
  }

  const thumbnail = getImageUrl(preview?.thumbnail);

  return (
    <button
      onClick={() => onClick?.(preview)}
      data-testid={`entity-card-${type}-${id}`}
      className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg ${colors.bg} ${colors.border} border ${colors.hover} transition-colors cursor-pointer ${className}`}
    >
      {thumbnail ? (
        <img src={thumbnail} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
      ) : (
        <div className={`w-8 h-8 rounded ${colors.accent} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      )}
      <div className="text-left min-w-0">
        <p className={`text-xs font-semibold ${colors.text} truncate max-w-[160px]`}>{preview?.name}</p>
        {preview?.subtitle && (
          <p className="text-[10px] text-gray-500 truncate max-w-[160px]">{preview.subtitle}</p>
        )}
      </div>
    </button>
  );
};

// Species-specific mini card with thumbnail (for autocomplete results)
export const SpeciesSearchCard = ({ species, onClick, className = "" }) => {
  const thumbnail = species.images?.[0] ? getImageUrl(species.images[0]) : null;

  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onClick?.(species);
      }}
      data-testid={`species-search-card-${species.id}`}
      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-violet-50 transition-colors ${className}`}
    >
      {thumbnail ? (
        <img src={thumbnail} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
      ) : (
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center flex-shrink-0">
          <Fish className="w-5 h-5 text-white" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate">{species.common_names?.[0] || species.scientific_name}</p>
        <p className="text-xs text-gray-500 italic truncate">{species.scientific_name}</p>
      </div>
    </button>
  );
};

// Parse text content and extract entity links
export const parseEntityLinks = (text) => {
  if (!text) return [];
  const parts = [];
  let lastIndex = 0;
  let match;

  const regex = new RegExp(ENTITY_LINK_REGEX.source, "g");
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", content: text.slice(lastIndex, match.index) });
    }
    parts.push({ type: "entity", entityType: match[1], entityId: match[2] });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push({ type: "text", content: text.slice(lastIndex) });
  }

  return parts;
};

// Check if text contains entity links
export const hasEntityLinks = (text) => {
  if (!text) return false;
  return ENTITY_LINK_REGEX.test(text);
};

// Generate entity link string for copying/pasting
export const createEntityLink = (type, id) => `[[${type}:${id}]]`;

export default EntityMiniCard;
